import {
  type FanResponseDto,
  OfChatMessagesService,
  type OnlyFansMessage,
  type VaultMediaItemDto,
} from '@api'
import qs from 'query-string'

import type { Creator } from '@/modules/creators/types'
import { apiClientChatting } from '@/utils/axios'
import { delay } from '@/utils/misc.ts'

import type {
  ClearChatRequestOptions,
  CommonChatActionsParams,
  CommonChatModificationParams,
  GetMessagesParams,
  GetMessagesResponse,
  GetUpcomingBirthdaysResponse,
  MultipartFinishRequest,
  MultipartStartResponse,
  MuteChatParams,
  SendMessageParams,
  SetDisplayNameParams,
  UploadMediaRequestOptions,
  UploadedFileDetails,
} from '../types'
import type { AITranslationLanguageType } from '../ui/chat-window/translation/types'

const isLoggedIn = (creatorId: number) => {
  return apiClientChatting
    .get(`/of-chat/${creatorId}/logged-in`)
    .then((res) => res.data.loggedIn as boolean)
}

const clearChat = ({
  creatorId,
  userId,
}: ClearChatRequestOptions): Promise<void> => {
  return apiClientChatting.delete(`/of-chat/${creatorId}/${userId}`)
}

const hideChat = async ({
  creatorId,
  userId,
}: CommonChatActionsParams): Promise<void> => {
  const res = await apiClientChatting.put(
    `/of-chat/${creatorId}/${userId}/hide`,
  )

  return res.data
}

const restrictChat = async ({
  creatorId,
  userId,
}: CommonChatActionsParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${userId}/restrict`,
  )

  return res.data
}

const unrestrictChat = async ({
  creatorId,
  userId,
}: CommonChatActionsParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${userId}/unrestrict`,
  )

  return res.data
}

const blockChat = async ({
  creatorId,
  userId,
}: CommonChatActionsParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${userId}/block`,
  )

  return res.data
}

const unblockChat = async ({
  creatorId,
  userId,
}: CommonChatActionsParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${userId}/unblock`,
  )

  return res.data
}

const getMessages = async ({
  anchorMessageId,
  fanId,
  creatorId,
  limit,
  direction,
}: GetMessagesParams): Promise<GetMessagesResponse> => {
  try {
    const res = await OfChatMessagesService.getChatMessages({
      creatorId,
      userId: fanId,
      anchorMessageId,
      limit,
      includeAnchor: true,
      descending: anchorMessageId ? direction === 'down' : true,
    })

    return {
      ...res,
      direction,
      fanId,
      anchorMessageId: anchorMessageId!,
      // @ts-ignore
      list: res.list,
    }
  } catch (error) {
    // eslint-disable-next-line no-throw-literal
    throw { error, direction }
  }
}

const setDisplayName = async ({
  creatorId,
  userId,
  name,
}: SetDisplayNameParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/user/${userId}/update`,
    { displayName: name },
  )

  return res.data
}

const sendMessage = async ({
  creatorId,
  userId,
  message,
}: SendMessageParams) => {
  const res = await apiClientChatting.post<OnlyFansMessage>(
    `/of-chat/${creatorId}/${userId}/message`,
    message,
  )

  return res.data
}

const muteChat = async ({
  creatorId,
  fanId,
  unmuteAt,
}: MuteChatParams): Promise<void> => {
  const params = qs.stringify(
    { unmuteAt },
    { skipNull: true, skipEmptyString: true },
  )
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${fanId}/mute?${params}`,
  )

  return res.data
}

const unmuteChat = async ({
  creatorId,
  fanId,
}: CommonChatModificationParams): Promise<void> => {
  const res = await apiClientChatting.post(
    `/of-chat/${creatorId}/${fanId}/unmute`,
  )

  return res.data
}

const getUpcomingBirthdays = (
  creatorId: number,
): Promise<GetUpcomingBirthdaysResponse> => {
  return apiClientChatting
    .get(`/of-chat/${creatorId}/upcomingBirthdays`)
    .then((res) => res.data)
}

const SIZE_OF_PART = 5 * 1024 * 1024
/**
 * Uploads media in chunks. If the file size is greater than 5MB, it uses a multipart upload strategy.
 *
 * @param {UploadMediaRequestOptions} options - The options for uploading media.
 * @param {number} options.creatorId - The ID of the creator uploading the media.
 * @param {string} options.fileName - The name of the file being uploaded.
 * @param {File} options.file - The file object to be uploaded.
 * @param {function} [options.onUploadProgress] - Optional callback function to track upload progress.
 *
 * @returns {Promise<UploadMediaResponse>} - A promise that resolves to the response of the upload operation.
 */
const uploadMediaInChunks = async ({
  creatorId,
  fileName,
  file,
  onUploadProgress,
  cancelToken,
  isVaultMedia,
  selectedCategoryId,
}: UploadMediaRequestOptions): Promise<
  VaultMediaItemDto | UploadedFileDetails
> => {
  const fileSize = file.size

  if (fileSize <= SIZE_OF_PART) {
    // Existing upload logic for files <= 5MB
    const formData = new FormData()

    formData.set('file', file)

    const query = qs.stringify({
      contentLength: fileSize,
      fileName,
      forcedProcessing: !!isVaultMedia,
      categoryId: selectedCategoryId,
    })

    const res = await apiClientChatting.post<
      VaultMediaItemDto | UploadedFileDetails
    >(`/of-chat/${creatorId}/media/upload?${query}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          onUploadProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total || 0,
          })
        }
      },
      cancelToken,
    })

    return res.data
  } else {
    // New multipart upload logic for files > 5MB
    const startRes = await apiClientChatting.post<MultipartStartResponse>(
      `/of-chat/${creatorId}/media/multipart/start`,
      {
        fileName,
        contentType: file.type,
        contentLength: fileSize,
      },
      {
        cancelToken,
      },
    )

    const { getUrl, uploadId, storageKey, keys } = startRes.data
    const parts: MultipartFinishRequest['parts'] = []
    const uploadPartProgress = new Map<number, number>()

    // Helper function to upload a single part
    const uploadPart = async (key: { part: number; putUrl: string }) => {
      const partBlob = file.slice(
        (key.part - 1) * SIZE_OF_PART,
        key.part * SIZE_OF_PART,
      )

      const formData = new FormData()

      formData.set('file', partBlob)

      const partRes = await apiClientChatting.post<{ ETag: string }>(
        `/of-chat/${creatorId}/media/multipart/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            fileName,
            contentType: file.type,
            part: key.part,
            putUrl: key.putUrl,
          },
          onUploadProgress: (progressEvent) => {
            if (onUploadProgress) {
              uploadPartProgress.set(key.part, progressEvent.loaded)

              const totalLoaded = Array.from(
                uploadPartProgress.values(),
              ).reduce((acc, curr) => acc + curr, 0)

              onUploadProgress({
                loaded: totalLoaded,
                total: fileSize,
              })
            }
          },
          cancelToken,
        },
      )

      parts.push({ PartNumber: key.part, ETag: partRes.data.ETag })
    }

    // Limit concurrent uploads to 5
    const maxConcurrentUploads = 5
    const uploadQueue = keys.slice() // Copy the keys array
    const activeUploads: Promise<void>[] = []

    while (uploadQueue.length > 0 || activeUploads.length > 0) {
      while (
        activeUploads.length < maxConcurrentUploads &&
        uploadQueue.length > 0
      ) {
        const key = uploadQueue.shift()

        if (key) {
          const uploadPromise = uploadPart(key).then(() => {
            // Remove the completed upload from active uploads
            activeUploads.splice(activeUploads.indexOf(uploadPromise), 1)
          })

          activeUploads.push(uploadPromise)
        }
      }
      // Wait for any of the active uploads to complete
      await Promise.race(activeUploads)
    }

    parts.sort((a, b) => a.PartNumber - b.PartNumber)

    // Finish the multipart upload
    const finishRes = await apiClientChatting.post<
      VaultMediaItemDto | UploadedFileDetails
    >(
      `/of-chat/${creatorId}/media/multipart/finish`,
      {
        storageKey,
        getUrl,
        uploadId,
        contentLength: fileSize,
        fileName,
        contentType: file.type,
        skipDuplicateCheck: false,
        forcedProcessing: !!isVaultMedia,
        includeWatermark: true,
        parts,
        categoryId: selectedCategoryId,
      },
      { cancelToken },
    )

    // TODO: should be removed after we implement server side vault cache
    // synthetic delay to allow OF to process the media
    await delay(1000)

    return finishRes.data
  }
}

const getUnreadChatsCount = (creatorId: number): Promise<number> =>
  apiClientChatting
    .get(`/of-chat/${creatorId}/count`)
    .then((res) => res.data.chatMessagesCount)

const translateInput = (
  creatorId: Creator['id'],
  language: AITranslationLanguageType,
  input: string,
) => {
  const query = qs.stringifyUrl(
    {
      url: `/translation/${creatorId}/translateText`,
      query: {
        language,
        initiationCause: 'Chat',
      },
    },
    {
      skipEmptyString: true,
      skipNull: true,
    },
  )

  return apiClientChatting
    .post<string[]>(query.toString(), [input])
    .then((res) => res.data[0])
}

const setTranslationLanguage = (
  creatorId: Creator['id'],
  userId: FanResponseDto['id'],
  language: AITranslationLanguageType | null,
) => {
  const query = qs.stringifyUrl(
    {
      url: `/translation/${creatorId}/${userId}/setLanguage`,
      query: {
        language: language ?? 'null',
      },
    },
    {
      skipEmptyString: true,
      skipNull: true,
    },
  )

  return apiClientChatting.post(query.toString())
}

export const chatsService = {
  clearChat,
  hideChat,
  restrictChat,
  unrestrictChat,
  blockChat,
  unblockChat,
  getMessages,
  setDisplayName,
  sendMessage,
  muteChat,
  unmuteChat,
  getUpcomingBirthdays,
  getUnreadChatsCount,
  isLoggedIn,
  translateInput,
  setTranslationLanguage,
  uploadMediaInChunks,
}
